import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getBaseApiUrl } from 'src/config/apiConfig';

import useApplicationGroups from '../hooks/useApplicationGroups';
import useEffectiveApplicationGroupId from '../hooks/useEffectiveApplicationGroupId';

export interface DynamicBrandConfig {
  primaryColor?: string;

  secondaryColor?: string;

  logoUrl?: string;

  logoLongUrl?: string;

  faviconUrl?: string;

  brandName: string;

  contactDetails?: {
    email?: string;
    website?: string;
    number?: string;
  };
}

interface DynamicBrandContextValue {
  config: DynamicBrandConfig;

  isActive: boolean;

  setConfig: (config: Partial<DynamicBrandConfig>) => void;

  clearConfig: () => void;

  resetToDefaults: () => void;

  drawerOpen: boolean;

  setDrawerOpen: (open: boolean) => void;

  selectedAppGroupId: string | null;
}

const DynamicBrandContext = createContext<DynamicBrandContextValue>({
  config: { brandName: 'Switch Energy' },
  isActive: false,
  setConfig: () => {},
  clearConfig: () => {},
  resetToDefaults: () => {},
  drawerOpen: false,
  setDrawerOpen: () => {},
  selectedAppGroupId: null,
});

function isConfigActive(config: DynamicBrandConfig): boolean {
  return !!(
    config.primaryColor ||
    config.secondaryColor ||
    config.logoUrl ||
    config.logoLongUrl ||
    config.faviconUrl
  );
}

function extractBrandConfigFromAppGroup(
  appGroup: any
): Partial<DynamicBrandConfig> {
  const brand = appGroup?.brand;
  if (!brand) return {};

  const config: Partial<DynamicBrandConfig> = {};

  if (appGroup.name) config.brandName = appGroup.name;

  if (brand.colors?.length >= 1) config.primaryColor = brand.colors[0];
  if (brand.colors?.length >= 2) config.secondaryColor = brand.colors[1];

  if (brand.contactDetails) {
    config.contactDetails = {
      email: brand.contactDetails.supportEmail,
      website: brand.contactDetails.website,
      number: brand.contactDetails.supportNumber,
    };
  }

  const appGroupId = appGroup._id;
  const baseUrl = `${getBaseApiUrl()}application-group/${appGroupId}/brand`;
  if (appGroupId && brand.assets?.favicon?.key) {
    config.logoUrl = `${baseUrl}/favicon`;
    config.faviconUrl = `${baseUrl}/favicon`;
  }
  if (appGroupId && brand.assets?.logo?.key) {
    config.logoLongUrl = `${baseUrl}/logo`;
  }

  return config;
}

interface DynamicBrandProviderProps {
  children: React.ReactNode;
}

const BRAND_SLUG = process.env.REACT_APP_BRAND;
const IS_WHITELABEL = BRAND_SLUG && BRAND_SLUG !== 'switch';
const BRAND_CACHE_KEY = `brandConfig_${BRAND_SLUG}`;

function mapBrandResponse(
  data: any,
  baseUrl: string
): Partial<DynamicBrandConfig> {
  const config: Partial<DynamicBrandConfig> = {};
  if (data.brandName) config.brandName = data.brandName;
  if (data.primaryColor) config.primaryColor = data.primaryColor;
  if (data.secondaryColor) config.secondaryColor = data.secondaryColor;
  if (data.contactDetails) {
    config.contactDetails = {
      email: data.contactDetails.supportEmail,
      website: data.contactDetails.website,
      number: data.contactDetails.supportNumber,
    };
  }
  if (data.faviconAsset) {
    config.logoUrl = `${baseUrl}${data.faviconAsset}`;
    config.faviconUrl = `${baseUrl}${data.faviconAsset}`;
  }
  if (data.logoAsset) {
    config.logoLongUrl = `${baseUrl}${data.logoAsset}`;
  }
  return config;
}

function loadCachedBrandConfig(): Partial<DynamicBrandConfig> {
  if (!IS_WHITELABEL) return {};
  try {
    const cached = localStorage.getItem(BRAND_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
}

export const DynamicBrandProvider: React.FC<DynamicBrandProviderProps> = ({
  children,
}) => {
  const { applicationGroups } = useApplicationGroups();
  const selectedAppGroupId = useEffectiveApplicationGroupId();

  const [localOverrides, setLocalOverrides] = useState<
    Partial<DynamicBrandConfig>
  >({});
  const [brandingSuppressed, setBrandingSuppressed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [preAuthConfig, setPreAuthConfig] = useState<
    Partial<DynamicBrandConfig>
  >(loadCachedBrandConfig);

  // Fetch brand config once using the REACT_APP_BRAND slug
  useEffect(() => {
    if (!IS_WHITELABEL) return;

    const baseUrl = getBaseApiUrl();
    fetch(`${baseUrl}application-group/${BRAND_SLUG}/brand`)
      .then((res) => {
        if (!res.ok)
          throw new Error(`Brand config fetch failed: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!data) return;
        const config = mapBrandResponse(data, baseUrl);
        localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(config));
        setPreAuthConfig(config);
      })
      .catch((err) => {
        console.error('Failed to load pre-auth brand config:', err);
      });
  }, []);

  const appGroupConfig = useMemo(() => {
    if (!selectedAppGroupId || !applicationGroups?.length) return {};
    const appGroup = applicationGroups.find(
      (ag: any) => ag._id === selectedAppGroupId
    );
    return extractBrandConfigFromAppGroup(appGroup);
  }, [selectedAppGroupId, applicationGroups]);

  useEffect(() => {
    setLocalOverrides({});
    setBrandingSuppressed(false);
  }, [selectedAppGroupId]);

  const mergedConfig = useMemo(() => {
    const merged = brandingSuppressed
      ? ({} as Partial<DynamicBrandConfig>)
      : ({
          ...preAuthConfig,
          ...appGroupConfig,
        } as Partial<DynamicBrandConfig>);

    if (localOverrides.primaryColor)
      merged.primaryColor = localOverrides.primaryColor;
    if (localOverrides.secondaryColor)
      merged.secondaryColor = localOverrides.secondaryColor;
    if (localOverrides.logoUrl) merged.logoUrl = localOverrides.logoUrl;
    if (localOverrides.logoLongUrl)
      merged.logoLongUrl = localOverrides.logoLongUrl;
    if (localOverrides.faviconUrl)
      merged.faviconUrl = localOverrides.faviconUrl;
    if (localOverrides.brandName) merged.brandName = localOverrides.brandName;
    if (localOverrides.contactDetails) {
      merged.contactDetails = {
        ...merged.contactDetails,
        ...localOverrides.contactDetails,
      };
    }

    merged.brandName ??= IS_WHITELABEL ? '' : 'Switch Energy';

    // When branding is active, ensure contactDetails is always defined so
    // consumers don't fall back to Switch defaults for missing fields.
    if (isConfigActive(merged as DynamicBrandConfig)) {
      merged.contactDetails ??= {};
    }

    return merged as DynamicBrandConfig;
  }, [preAuthConfig, appGroupConfig, localOverrides, brandingSuppressed]);

  const isActive = useMemo(() => isConfigActive(mergedConfig), [mergedConfig]);

  const setConfig = useCallback((update: Partial<DynamicBrandConfig>) => {
    setBrandingSuppressed(false);
    setLocalOverrides((prev) => ({ ...prev, ...update }));
  }, []);

  const clearConfig = useCallback(() => {
    setLocalOverrides({});
  }, []);

  const resetToDefaults = useCallback(() => {
    setLocalOverrides({});
    setBrandingSuppressed(true);
  }, []);

  const value = useMemo(
    () => ({
      config: mergedConfig,
      isActive,
      setConfig,
      clearConfig,
      resetToDefaults,
      drawerOpen,
      setDrawerOpen,
      selectedAppGroupId: selectedAppGroupId || null,
    }),
    [
      mergedConfig,
      isActive,
      setConfig,
      clearConfig,
      resetToDefaults,
      drawerOpen,
      selectedAppGroupId,
    ]
  );

  return (
    <DynamicBrandContext.Provider value={value}>
      {children}
    </DynamicBrandContext.Provider>
  );
};

export const useDynamicBrand = () => useContext(DynamicBrandContext);
